import { supabase } from '../supabaseClient'

/**
 * Extrai mensagem legível quando a Edge Function retorna HTTP 4xx/5xx.
 */
async function extrairErroEdgeFunction(error, data) {
  if (data?.error) {
    return typeof data.error === 'string' ? data.error : (data.error?.message || String(data.error))
  }
  try {
    if (error?.context && typeof error.context.json === 'function') {
      const body = await error.context.json()
      if (body?.error) return typeof body.error === 'string' ? body.error : String(body.error)
      if (body?.message) return typeof body.message === 'string' ? body.message : String(body.message)
    }
  } catch (parseErr) {
    console.warn('[Baixa Vistoria] Não foi possível ler corpo do erro da função:', parseErr)
  }
  const msg = error?.message || ''
  if (msg && !msg.includes('non-2xx')) return msg
  return 'Falha na Edge Function. Verifique no Supabase: secrets RESEND_API_KEY e RESEND_FROM_EMAIL, deploy da função send-vistoria-baixa-email e e-mail do usuário em usuarios_sistema.'
}

/**
 * Envia e-mail de confirmação da baixa (Resend): destinatário = usuário logado; CC = demais responsáveis da OS.
 */
export async function enviarEmailBaixaVistoria(params) {
  try {
    const { data, error } = await supabase.functions.invoke('send-vistoria-baixa-email', {
      body: params
    })

    if (data && (data.error || data.ok === false)) {
      return { success: false, error: data.error || 'Erro ao enviar e-mail.' }
    }
    if (error) {
      const msg = await extrairErroEdgeFunction(error, data)
      console.error('[Baixa Vistoria] Erro da Edge Function:', msg, { error, data })
      return { success: false, error: msg }
    }
    return {
      success: true,
      message: data?.message || 'E-mail enviado com sucesso.'
    }
  } catch (e) {
    console.error('[Baixa Vistoria] Erro ao enviar e-mail:', e)
    return {
      success: false,
      error: (e && e.message) || 'Falha ao enviar e-mail de confirmação.'
    }
  }
}

/** Extrai lista de URLs de um campo (string, JSON array ou array). */
function parseImageUrls(value) {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.map((u) => (typeof u === 'string' ? u : u?.url)).filter(Boolean)
  }
  const str = String(value).trim()
  if (!str) return []
  try {
    const parsed = JSON.parse(str)
    if (Array.isArray(parsed)) {
      return parsed.map((u) => (typeof u === 'string' ? u : u?.url)).filter(Boolean)
    }
  } catch {
    /* valor único */
  }
  return [str]
}

/**
 * Coleta fotos/links do formulário de baixa para o e-mail.
 */
export function coletarFotosBaixa(formBaixa, imageFiles = {}, multipleImageFiles = {}) {
  const fotos = []

  const addGrupo = (titulo, urls) => {
    const lista = [...new Set((urls || []).map((u) => String(u).trim()).filter(Boolean))]
    if (lista.length > 0) fotos.push({ titulo, urls: lista })
  }

  addGrupo('Padrão', [
    formBaixa.caminho_img_padrao,
    imageFiles.padrao?.url
  ])
  addGrupo('Inversor', [
    formBaixa.caminho_img_inversor,
    imageFiles.inversor?.url
  ])
  addGrupo('Foto da fachada', [
    formBaixa.caminho_img_fachada,
    imageFiles.fachada?.url
  ])
  addGrupo('Mapa do local', [
    formBaixa.caminho_img_mapa,
    imageFiles.mapa?.url
  ])
  addGrupo('Quadro', [
    formBaixa.caminho_img_quadro,
    imageFiles.quadro?.url
  ])
  addGrupo('Croqui', [
    formBaixa.caminho_img_croqui,
    imageFiles.croqui?.url
  ])

  let telhadoUrls = parseImageUrls(formBaixa.caminho_img_telhado)
  if (telhadoUrls.length === 0 && multipleImageFiles.telhado?.length) {
    telhadoUrls = multipleImageFiles.telhado.map((i) => i.url).filter(Boolean)
  }
  addGrupo('Telhado', telhadoUrls)

  let estruturaUrls = parseImageUrls(formBaixa.caminho_img_estrutura)
  if (estruturaUrls.length === 0 && multipleImageFiles.estrutura?.length) {
    estruturaUrls = multipleImageFiles.estrutura.map((i) => i.url).filter(Boolean)
  }
  addGrupo('Estrutura', estruturaUrls)

  return fotos
}

/**
 * Monta o payload do e-mail a partir do formulário de baixa.
 */
export function buildBaixaEmailPayload({
  userId,
  vistoriaParaBaixa,
  formBaixa,
  tiposPadrao,
  vistoriaId,
  atualizacao,
  imageFiles,
  multipleImageFiles
}) {
  const tipoPadrao = (tiposPadrao || []).find((t) => String(t.id) === String(formBaixa.tipo_padrao_id))
  const clienteNome =
    vistoriaParaBaixa?.cliente?.nome_completo ||
    (Array.isArray(vistoriaParaBaixa?.cliente) ? vistoriaParaBaixa.cliente[0]?.nome_completo : null) ||
    ''

  return {
    id_usuario: userId,
    cliente_nome: clienteNome,
    vistoria_id: vistoriaId || vistoriaParaBaixa?.vistoria_id || null,
    ordem_id: vistoriaParaBaixa?.id || null,
    atualizacao: !!atualizacao,
    data_vistoria: formBaixa.data_vistoria || null,
    status: formBaixa.status || null,
    tipo_padrao: tipoPadrao?.nome || formBaixa.tipo_padrao_id || null,
    ligacao_padrao: formBaixa.ligacao_padrao || null,
    local_inversor: formBaixa.local_inversor || null,
    posicao_poste_concessionaria: formBaixa.posicao_poste_concessionaria || null,
    conexao_inversor: formBaixa.conexao_inversor || null,
    local_aterramento_sistema: formBaixa.local_aterramento_sistema || null,
    metragem_ca_inversor: formBaixa.metragem_ca_inversor || null,
    metragem_cc: formBaixa.metragem_cc || null,
    metragem_ca_padrao_fases: formBaixa.metragem_ca_padrao_fases || null,
    percurso_aterramento: formBaixa.percurso_aterramento || null,
    qtd_conduletes_inversor: formBaixa.qtd_conduletes_inversor || null,
    qtd_eletroduto_inversor: formBaixa.qtd_eletroduto_inversor || null,
    qtd_eletroduto_padrao: formBaixa.qtd_eletroduto_padrao || null,
    qtd_modulos_telhado: formBaixa.qtd_modulos_telhado || null,
    observacoes: formBaixa.observacoes || null,
    fotos: coletarFotosBaixa(formBaixa, imageFiles, multipleImageFiles)
  }
}
